import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import './HRLiveBoard.css';

const CITIES = ['All Cities', 'Lahore', 'Karachi', 'Islamabad', 'Multan'];
const DEPTS  = ['All Depts', 'Accounts', 'Sales', 'Tech / Marketing', 'Warehouse'];

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarClass(name) {
  const c = ['av-blue','av-teal','av-amber','av-purple','av-coral','av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatDuration(clockInIso) {
  if (!clockInIso) return '—';
  const diff = Math.floor((Date.now() - new Date(clockInIso)) / 1000);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function getLateTag(lateMinutes) {
  if (!lateMinutes || lateMinutes === 0) return { label: 'On Time', type: 'ontime' };
  if (lateMinutes <= 30)  return { label: `+${lateMinutes}m`, type: 'minor' };
  if (lateMinutes <= 60)  return { label: `+${lateMinutes}m`, type: 'major' };
  return { label: `+${lateMinutes}m`, type: 'very-late' };
}

function LiveDuration({ clockIn, isOut }) {
  const [dur, setDur] = useState(formatDuration(clockIn));
  useEffect(() => {
    if (isOut) return;
    const t = setInterval(() => setDur(formatDuration(clockIn)), 1000);
    return () => clearInterval(t);
  }, [clockIn, isOut]);
  return (
    <span className={`lb-duration ${isOut ? 'lb-dur-done' : 'lb-dur-live'}`}>
      {isOut ? formatDuration(clockIn) : dur}
    </span>
  );
}

export default function HRLiveBoard() {
  const [records, setRecords]         = useState([]);
  const [employees, setEmployees]     = useState([]);
  const [summary, setSummary]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [cityFilter, setCityFilter]   = useState('All Cities');
  const [deptFilter, setDeptFilter]   = useState('All Depts');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);
  const [view, setView]               = useState('table'); // 'table' | 'cards'
  const timerRef = useRef(null);

  const empMap = {};
  employees.forEach(e => { empMap[e.id] = e; });

  const fetchData = useCallback(async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      const [attRes, empRes] = await Promise.all([
        api.getAllAttendance(),
        api.getAllEmployees(),
      ]);
      setRecords(attRes.records || []);
      setSummary(attRes.summary || null);
      setEmployees(empRes.employees || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to load live data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(() => fetchData(), 60000);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  const totalEmployees = employees.length;
  const clockedIn      = summary?.clocked_in  || 0;
  const clockedOut     = summary?.clocked_out || 0;
  const lateCount      = summary?.late        || 0;
  const totalPresent   = summary?.total       || 0;
  const absent         = Math.max(0, totalEmployees - totalPresent);
  const attendanceRate = totalEmployees > 0 ? Math.round((totalPresent / totalEmployees) * 100) : 0;

  // Enrich records with employee data
  const enriched = records.map(r => ({
    ...r,
    department: empMap[r.employee_id]?.department_name ||
                empMap[r.employee_id]?.department || r.department || '—',
    shift_name: empMap[r.employee_id]?.shift_name || '—',
  }));

  // Filters
  let filtered = [...enriched];
  if (cityFilter !== 'All Cities') filtered = filtered.filter(r => r.city === cityFilter);
  if (deptFilter !== 'All Depts')  filtered = filtered.filter(r => r.department === deptFilter);
  if (statusFilter === 'In')  filtered = filtered.filter(r => r.clock_in && !r.clock_out);
  if (statusFilter === 'Out') filtered = filtered.filter(r => r.clock_in && r.clock_out);
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(r =>
      (r.full_name     || '').toLowerCase().includes(q) ||
      (r.emp_code      || '').toLowerCase().includes(q) ||
      (r.location_name || '').toLowerCase().includes(q) ||
      (r.department    || '').toLowerCase().includes(q)
    );
  }

  // Sort: active first, then by clock-in time
  filtered.sort((a, b) => {
    if (a.clock_out && !b.clock_out) return 1;
    if (!a.clock_out && b.clock_out) return -1;
    return new Date(a.clock_in) - new Date(b.clock_in);
  });

  if (loading) {
    return (
      <div className="lb-loading">
        <div className="lb-spinner" />
        <p>Loading live board…</p>
      </div>
    );
  }

  return (
    <div className="lb-root">

      {/* Header */}
      <div className="lb-header">
        <div className="lb-header-left">
          <div className="lb-title-row">
            <h1 className="lb-title">Live Board</h1>
            <span className="lb-live-dot">● LIVE</span>
          </div>
          <span className="lb-date">
            {new Date().toLocaleDateString('en-PK', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            })}
          </span>
        </div>
        <div className="lb-header-right">
          {lastRefresh && (
            <span className="lb-refresh-time">
              Updated {lastRefresh.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </span>
          )}
          <div className="lb-view-toggle">
            <button className={`lb-view-btn ${view === 'table' ? 'lb-view-active' : ''}`}
              onClick={() => setView('table')}>☰ Table</button>
            <button className={`lb-view-btn ${view === 'cards' ? 'lb-view-active' : ''}`}
              onClick={() => setView('cards')}>⊞ Cards</button>
          </div>
          <button
            className={`lb-refresh-btn ${refreshing ? 'lb-refreshing' : ''}`}
            onClick={() => fetchData(true)} disabled={refreshing}
          >
            {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="lb-error">{error}</div>}

      {/* Stats */}
      <div className="lb-stats">
        <div className="lb-stat lb-stat-total">
          <span className="lb-stat-num">{totalEmployees}</span>
          <span className="lb-stat-lbl">Total</span>
        </div>
        <div className="lb-stat lb-stat-in">
          <span className="lb-stat-pulse" />
          <span className="lb-stat-num">{clockedIn}</span>
          <span className="lb-stat-lbl">Active Now</span>
        </div>
        <div className="lb-stat lb-stat-out">
          <span className="lb-stat-num">{clockedOut}</span>
          <span className="lb-stat-lbl">Clocked Out</span>
        </div>
        <div className="lb-stat lb-stat-absent">
          <span className="lb-stat-num">{absent}</span>
          <span className="lb-stat-lbl">Not In</span>
        </div>
        <div className="lb-stat lb-stat-late">
          <span className="lb-stat-num">{lateCount}</span>
          <span className="lb-stat-lbl">Late</span>
        </div>
        <div className="lb-stat lb-stat-rate">
          <span className="lb-stat-num">{attendanceRate}%</span>
          <span className="lb-stat-lbl">Rate</span>
        </div>
      </div>

      {/* Filters */}
      <div className="lb-filters">
        <input className="lb-search" type="text"
          placeholder="Search name, ID, location, department…"
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        <div className="lb-filter-group">
          {CITIES.map(c => (
            <button key={c}
              className={`lb-filter-btn ${cityFilter === c ? 'lb-filter-active' : ''}`}
              onClick={() => setCityFilter(c)}
            >{c}</button>
          ))}
        </div>
        <div className="lb-filter-group">
          {DEPTS.map(d => (
            <button key={d}
              className={`lb-filter-btn ${deptFilter === d ? 'lb-filter-active' : ''}`}
              onClick={() => setDeptFilter(d)}
            >{d === 'All Depts' ? d : d.split(' ')[0]}</button>
          ))}
        </div>
        <div className="lb-filter-group">
          {[
            { key: 'All', label: 'All' },
            { key: 'In',  label: '● In' },
            { key: 'Out', label: '○ Out' },
          ].map(s => (
            <button key={s.key}
              className={`lb-filter-btn ${statusFilter === s.key ? 'lb-filter-active' : ''}`}
              onClick={() => setStatusFilter(s.key)}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="lb-empty-state">
          <div className="lb-empty-icon">📋</div>
          <div className="lb-empty-title">No records found</div>
          <div className="lb-empty-sub">
            {searchQuery || cityFilter !== 'All Cities' || deptFilter !== 'All Depts' || statusFilter !== 'All'
              ? 'Try adjusting your filters'
              : 'No attendance records for today yet'}
          </div>
        </div>
      ) : view === 'table' ? (

        /* TABLE VIEW */
        <div className="lb-table-wrap">
          <table className="lb-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Location</th>
                <th>Clock In</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Clock Out</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => {
                const isOut    = !!r.clock_out;
                const lateTag  = getLateTag(r.late_minutes);
                return (
                  <tr key={r.id || idx} className={`lb-row ${isOut ? 'lb-row-out' : 'lb-row-in'}`}>
                    <td>
                      <div className="lb-emp-cell">
                        <div className={`lb-avatar ${getAvatarClass(r.full_name)}`}>
                          {getInitials(r.full_name)}
                        </div>
                        <div className="lb-emp-info">
                          <span className="lb-emp-name">{r.full_name}</span>
                          <span className="lb-emp-code">{r.emp_code}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="lb-dept-tag">{r.department || '—'}</span>
                    </td>
                    <td>
                      <div className="lb-loc-cell">
                        <span className="lb-loc-name">{r.location_name || '—'}</span>
                        <span className="lb-city-tag">{r.city || ''}</span>
                      </div>
                    </td>
                    <td>
                      <span className="lb-time">{formatTime(r.clock_in)}</span>
                    </td>
                    <td>
                      <div className="lb-status-cell">
                        {isOut ? (
                          <span className="lb-badge lb-badge-out">Clocked Out</span>
                        ) : (
                          <span className="lb-badge lb-badge-in">● Active</span>
                        )}
                        {!isOut && (
                          <span className={`lb-late-tag lb-late-${lateTag.type}`}>
                            {lateTag.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <LiveDuration clockIn={r.clock_in} isOut={isOut} />
                    </td>
                    <td>
                      <span className="lb-time lb-time-muted">
                        {r.clock_out ? formatTime(r.clock_out) : '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      ) : (

        /* CARD VIEW */
        <div className="lb-cards-grid">
          {filtered.map((r, idx) => {
            const isOut   = !!r.clock_out;
            const lateTag = getLateTag(r.late_minutes);
            return (
              <div key={r.id || idx} className={`lb-card ${isOut ? 'lb-card-out' : 'lb-card-in'}`}>
                <div className="lb-card-top">
                  <div className={`lb-avatar lb-avatar-lg ${getAvatarClass(r.full_name)}`}>
                    {getInitials(r.full_name)}
                  </div>
                  <div className="lb-card-status">
                    {isOut ? (
                      <span className="lb-badge lb-badge-out">Out</span>
                    ) : (
                      <span className="lb-badge lb-badge-in">● In</span>
                    )}
                  </div>
                </div>
                <div className="lb-card-name">{r.full_name}</div>
                <div className="lb-card-code">{r.emp_code}</div>
                <div className="lb-card-dept">{r.department || '—'}</div>
                <div className="lb-card-loc">{r.location_name} · {r.city}</div>
                <div className="lb-card-times">
                  <div className="lb-card-time-item">
                    <span className="lb-card-time-lbl">In</span>
                    <span className="lb-card-time-val">{formatTime(r.clock_in)}</span>
                  </div>
                  {!isOut && (
                    <div className="lb-card-time-item">
                      <span className="lb-card-time-lbl">Duration</span>
                      <span className="lb-card-time-val lb-dur-live">
                        <LiveDuration clockIn={r.clock_in} isOut={false} />
                      </span>
                    </div>
                  )}
                  {isOut && (
                    <div className="lb-card-time-item">
                      <span className="lb-card-time-lbl">Out</span>
                      <span className="lb-card-time-val">{formatTime(r.clock_out)}</span>
                    </div>
                  )}
                </div>
                {!isOut && lateTag.type !== 'ontime' && (
                  <div className={`lb-card-late lb-late-${lateTag.type}`}>
                    {lateTag.label} late
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="lb-footer">
        Showing {filtered.length} of {records.length} records · Auto-refreshes every 60s
      </div>
    </div>
  );
}